import type { DraggableCanvasCardData, DraggableData } from "@/dnd/ids";
import {
  buildFilesRailDragPayload,
  type FilesRailDragFileResource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/buildFilesRailDragPayload";
import type { RailRegisteredResource } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { buildCanvasLinkEntity } from "@/pages/canvas/utils/canvasLinkEntity";
import type { Canvas } from "@cohesive/api-client";

/**
 * Build the drag payload for a workspaces-rail canvas row.
 *
 * - Homogeneous multi-drag includes every selected canvas only when they all
 *   share the dragged row's workspace.
 * - A MIXED files+canvases selection becomes a `files` draggable with the
 *   canvases riding as `canvasIds` — one tree, one drag (the dispatcher moves
 *   files and canvases together on workspace destinations).
 * - Anything else (chats/skills in the selection, cross-workspace canvas
 *   sets) fails closed to the single dragged canvas.
 */
export function buildWorkspacesRailCanvasDragPayload(
  canvas: Canvas,
  selectedResources: RailRegisteredResource[],
  options: { rowSelected: boolean; sourcePath?: string },
): DraggableData {
  const label = canvas.name || "Untitled";
  const sourceWorkspaceId = canvas.workspace_id ?? "";
  const selectedCanvases = selectedResources.filter((r) => r.kind === "canvas");
  const selectedFiles = selectedResources.filter(
    (r): r is Extract<RailRegisteredResource, { kind: "file" }> =>
      r.kind === "file",
  );

  const filesAndCanvasesOnly = selectedResources.every(
    (r) => r.kind === "file" || r.kind === "canvas",
  );
  if (
    options.rowSelected &&
    filesAndCanvasesOnly &&
    selectedFiles.length > 0 &&
    selectedCanvases.length > 0
  ) {
    const canvasIds = selectedCanvases.map((r) => r.data.id);
    const total = selectedFiles.length + canvasIds.length;
    return {
      kind: "files",
      payloads: selectedFiles.map((r) =>
        buildFilesRailDragPayload(
          r.data,
          r.data.workspace_id ?? sourceWorkspaceId,
          { sharedSource: (r as FilesRailDragFileResource).sharedSource },
        ),
      ),
      canvasIds,
      primaryName: `${total} items`,
    };
  }

  const inMulti = options.rowSelected && selectedCanvases.length > 1;
  const allSameWorkspaceCanvas =
    inMulti &&
    selectedResources.every((r) => r.kind === "canvas") &&
    selectedCanvases.every(
      (r) => (r.data.workspace_id ?? "") === sourceWorkspaceId,
    );
  const canvases: Canvas[] = allSameWorkspaceCanvas
    ? selectedCanvases.map((r) => r.data)
    : [canvas];

  const cardPayload: DraggableCanvasCardData = {
    kind: "canvas-card",
    canvasIds: canvases.map((c) => c.id),
    sourceWorkspaceId: canvases[0]?.workspace_id ?? sourceWorkspaceId,
    // Folder provenance is single-canvas only (see DraggableCanvasCardData) —
    // multi-drag must leave it undefined so root drops still run the move.
    // Surfaces that render a tree row pass its parent path; Favorites, Shared
    // and search rows have no row and leave it unknown.
    sourcePath: canvases.length === 1 ? options.sourcePath : undefined,
    primaryName: canvases.length === 1 ? label : `${canvases.length} canvases`,
    entities: canvases.map((c) => buildCanvasLinkEntity(c)),
  };
  return cardPayload;
}
